import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  type StorageReference,
  updateMetadata,
} from "firebase/storage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getMediaObjects, handleUpscale, storage } from "senf-shared";
import type { Project } from "senf-shared/types";
import { formatBytes, getFileTypeLabel } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import type { useFileCache } from "../../../hooks/useFileCache";
import type { MediaFile } from "../../../types/media";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import FileDrop from "../../core/fileDrop/FileDrop";
import Icon from "../../core/icon/Icon";
import type { IconName } from "../../core/icon/names";
import Toast from "../../core/toast/Toast";
import TruncateMiddle from "../../core/typography/TruncateMiddle";
import type { ImageInput } from "./ImagesUploadModal";

export const getFileIcon = (fileType?: string, multiple = false): IconName => {
  if (!fileType) return multiple ? "file/file-28-versions" : "file/file-28";
  if (fileType === "application/zip") return "file/zip-28";
  if (fileType === "application/pdf")
    return multiple ? "file/pdf-28-versions" : "file/pdf-28";
  if (fileType === "application/json")
    return multiple ? "file/json-28-versions" : "file/json-28";
  if (fileType === "text/csv")
    return multiple ? "file/table-28-versions" : "file/table-28";
  if (
    fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    fileType === "application/vnd.ms-excel"
  ) {
    return multiple ? "file/excel-28-versions" : "file/excel-28";
  }
  if (
    fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    fileType === "application/msword"
  ) {
    return multiple ? "file/word-28-versions" : "file/word-28";
  }
  if (
    fileType ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    fileType === "application/vnd.ms-powerpoint"
  ) {
    return multiple ? "file/powerpoint-28-versions" : "file/powerpoint-28";
  }

  if (fileType.startsWith("image/svg"))
    return multiple ? "file/vector-28-versions" : "file/vector-28";
  if (fileType.startsWith("image"))
    return multiple ? "file/image-28-versions" : "file/image-28";
  if (fileType.startsWith("video"))
    return multiple ? "file/video-28-versions" : "file/video-28";
  if (fileType.startsWith("audio"))
    return multiple ? "file/audio-28-versions" : "file/audio-28";

  return multiple ? "file/file-28-versions" : "file/file-28";
};
// Define a constant for the storage key
export const THUMBNAIL_CACHE_STORAGE_KEY = "senf-media-browser-thumbnail-cache";

export const sortMediaBrowserFiles = (a: MediaFile, b: MediaFile) => {
  if (a.type === "folder" && b.type !== "folder") return 1;
  if (a.type !== "folder" && b.type === "folder") return -1;
  return a.date && b.date
    ? new Date(b.date).getTime() - new Date(a.date).getTime()
    : 0;
};

// Tailwind variants for the component
const containerStyles = tv({
  base: "@container w-full h-full flex flex-col overflow-hidden relative",
});

const fileItemStyles = tv({
  base: "group/item relative cursor-pointer rounded-lg transition-all flex w-full aspect-square text-text-placeholder bg-surface-subdued ring-1 ring-border-subdued hover:ring-border-highlight-default hover:bg-surface-highlight-subdued-hovered hover:text-surface-highlight-default items-center justify-center text-5xl duration-75",
  variants: {
    selected: {
      true: "ring-border-highlight-default bg-surface-highlight-subdued-hovered text-surface-highlight-default",
    },
    disabled: {
      true: "opacity-50 cursor-default hover:ring-border-subdued hover:bg-surface-subdued hover:text-text-placeholder",
    },
  },
  defaultVariants: {
    selected: false,
    disabled: false,
  },
});

const fileDropStyles = tv({
  base: "absolute inset-[7px] w-auto h-auto !rounded-b-2xl z-20 duration-500 ease-expressive-out",
});

const loadingOverlayStyles = tv({
  base: "absolute inset-0 flex flex-1 justify-center items-center z-10",
});

const fileImageContainerStyles = tv({
  base: "flex items-center p-3 justify-center w-full aspect-square",
});

const fileImageStyles = tv({
  base: "max-w-full max-h-full object-contain rounded-sm transition-opacity duration-200 text-xs",
  variants: {
    disabled: {
      true: "opacity-50 grayscale",
    },
    loading: {
      true: "opacity-0",
    },
  },
});

const fileMetaStyles = tv({
  base: "text-xs text-gray-500",
  variants: {
    disabled: {
      true: "text-text-disabled",
    },
  },
});

const sectionHeaderStyles = tv({
  base: "text-sm col-span-full text-text-subdued font-bold sticky -top-5 bg-surface-subdued z-10 -mx-5 px-5 py-3 z-30",
});

export const Checkmark = ({ checked, className = "" }) => {
  if (!checked) return null;
  return (
    <div
      className={cn(
        "absolute top-0 right-0 m-3 flex items-center justify-center h-5 w-5 bg-action-primary-default ring-1 ring-white/50 rounded-full",
        className,
      )}
    >
      <Icon name={"checkmark-md"} className="text-text-on-primary" />
    </div>
  );
};

interface MediaBrowserProps {
  basePath: string;
  saveToPath?: string;
  currentPath?: string;
  currentImageUrl?: string | string[];
  maxItems?: number;
  showMultipleToggle?: boolean;
  initialSelected?: { url: string; alt: string; name: string; size: number }[];
  onSubmit?: (
    files: { url: string; alt: string; name: string; size: number }[],
  ) => void;
  columns?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  className?: string;
  fileTypeLabel?: {
    singular: string;
    plural: string;
  };
  fileTypes?: string[];
  userId: string;
  selectedFiles: ImageInput[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<ImageInput[]>>;
  fileCacheHook: ReturnType<typeof useFileCache>;
  project?: Project;
}

// Add this type to help with the getMediaObjects return value
interface MediaObject {
  url: string;
  name: string;
  path: string;
  size: number;
  width?: number;
  height?: number;
  alt?: string;
  // Use a more specific type for additional properties
  [key: string]: string | number | boolean | undefined;
}

function scaleImageToBlob(
  imageUrl: string,
  mimeType = "image/jpeg",
  options?: { maxSize?: number; quality?: number },
) {
  return new Promise<Blob>((resolve, reject) => {
    fetch(imageUrl)
      .then((res) => res.blob())
      .then((blob) => createImageBitmap(blob))
      .then((img) => {
        const maxSize = options?.maxSize || 256;
        const quality = options?.quality || 0.8;
        // Calculate new dimensions
        const newWidth =
          img.width > img.height ? maxSize : (maxSize / img.height) * img.width;
        const newHeight =
          img.width > img.height ? (maxSize / img.width) * img.height : maxSize;

        // Use OffscreenCanvas for better performance if available
        const canvas =
          typeof OffscreenCanvas !== "undefined"
            ? new OffscreenCanvas(newWidth, newHeight)
            : document.createElement("canvas");

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw the scaled-down image
        (ctx as CanvasRenderingContext2D).drawImage(
          img,
          0,
          0,
          newWidth,
          newHeight,
        );

        // Handle different canvas types
        if (canvas instanceof OffscreenCanvas) {
          canvas
            .convertToBlob({ type: mimeType, quality })
            .then(resolve)
            .catch(reject);
        } else {
          // For regular HTMLCanvasElement
          (canvas as HTMLCanvasElement).toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to convert canvas to Blob"));
            },
            mimeType,
            quality,
          );
        }
      })
      .catch(reject);
  });
}

type FileItemProps = {
  file: MediaFile;
  disabled?: boolean;
  basePath: string;
  onSelect: (file: MediaFile) => void;
};
// Extracted FileItem component
const FileItem = React.memo<FileItemProps & { id: string; selected: boolean }>(
  ({ file, id, selected, disabled = false, basePath, onSelect }) => {
    const [loading, setLoading] = useState(true);
    const [thumbnail, setThumbnail] = useState<string | undefined>();

    // Add a helper function to load thumbnails when needed
    const loadFileThumbnail = useCallback(
      async (file: MediaFile, forceReload = false) => {
        // Skip if not an image
        if (!file.type?.startsWith("image/")) {
          setThumbnail(undefined);
          setLoading(false);
          return;
        }

        if (forceReload) {
          setLoading(true);
          setThumbnail(undefined);
          // window.sessionStorage.setItem(THUMBNAIL_CACHE_STORAGE_KEY, '{}');
        }

        const thumbnails = JSON.parse(
          window.sessionStorage.getItem(THUMBNAIL_CACHE_STORAGE_KEY) || "{}",
        );
        const cachedThumbnail = thumbnails[file.url];
        // Skip if already loaded
        if (cachedThumbnail && !forceReload) {
          setThumbnail(cachedThumbnail);
          setLoading(false);
          return;
        }

        try {
          // Create a thumbnail from the original image
          const thumbnail = URL.createObjectURL(
            await scaleImageToBlob(file.url, file.type),
          );

          window.sessionStorage.setItem(
            THUMBNAIL_CACHE_STORAGE_KEY,
            JSON.stringify({
              ...JSON.parse(
                window.sessionStorage.getItem(THUMBNAIL_CACHE_STORAGE_KEY) ||
                  "{}",
              ),
              [file.url]: thumbnail,
            }),
          );

          setThumbnail(thumbnail);
          return;
        } catch (err) {
          console.warn("Failed to load thumbnail:", err);
          setThumbnail(undefined);
          return;
        } finally {
          setLoading(false);
        }
      },
      [],
    );

    useEffect(() => {
      loadFileThumbnail(file);
    }, [file, loadFileThumbnail]);

    // Handle select with useCallback to avoid recreation on each render
    const handleSelect = useCallback(() => {
      if (!disabled) onSelect(file);
    }, [disabled, file, onSelect]);

    // Handle keyboard events with useCallback
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          onSelect(file);
        }
      },
      [disabled, file, onSelect],
    );

    const fileIcon = useMemo<IconName>(
      () => getFileIcon(file.type),
      [file.type],
    );

    return (
      <div id={id}>
        <button
          type="button"
          className={fileItemStyles({
            selected,
            disabled,
          })}
          onClick={handleSelect}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          aria-selected={selected}
          aria-disabled={disabled}
        >
          {file.type === "folder" ? (
            <>
              <Icon
                name="folder-open"
                className="w-[1em] h-[1em] hidden group-hover/item:inline"
              />
              <Icon
                name="folder-closed"
                className="w-[1em] h-[1em] inline group-hover/item:hidden"
              />
            </>
          ) : file.type.startsWith("image") ? (
            <div className={fileImageContainerStyles()}>
              <Checkmark checked={selected} className="m-2 z-20" />
              <img
                src={thumbnail}
                alt={file.name}
                onError={() => loadFileThumbnail(file, true)}
                className={fileImageStyles({
                  disabled,
                  loading: !thumbnail || loading,
                })}
              />
              {/* {(!thumbnail|| loading) && <div className="text-surface-depressed text-5xl absolute"><Icon name="image" className="w-[1em] h-[1em]" /></div>} */}
            </div>
          ) : (
            <div className={fileImageContainerStyles()}>
              <Checkmark checked={selected} className="m-2 z-20" />
              <Icon
                name={fileIcon}
                className={cn("w-[1em] h-[1em]", disabled && "grayscale")}
              />
            </div>
          )}
        </button>
        <div className="py-2">
          <TruncateMiddle
            text={file.name}
            className={cn("text-sm", disabled && "text-text-disabled")}
          />
          {(file.size || file.itemCount) && (
            <span className={fileMetaStyles({ disabled })}>
              {file.size ? formatBytes(file.size) : file.itemCount}
            </span>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary rerenders
    return (
      prevProps.file.url === nextProps.file.url &&
      prevProps.selected === nextProps.selected &&
      prevProps.disabled === nextProps.disabled &&
      // Only rerender if the relevant functions have changed
      prevProps.onSelect === nextProps.onSelect
    );
  },
);

// Add this right after the import statements
// This is a wrapper component that isolates selection state

const MemoizedFileItem = React.memo(
  ({ file, isSelected, ...props }: FileItemProps & { isSelected: boolean }) => {
    return (
      <FileItem file={file} id={file.url} selected={isSelected} {...props} />
    );
  },
  (prevProps, nextProps) => {
    // Only rerender if these properties changed
    return (
      prevProps.file.url === nextProps.file.url &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.onSelect === nextProps.onSelect
    );
  },
);

const groupFilesByDate = (files: MediaFile[]) => {
  const now = new Date();
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const oneWeekAgo = new Date(now.getTime() - 7 * oneDay);

  // Create our date categories
  const groups: Record<string, MediaFile[]> = {
    recent: [], // Last 7 days
  };

  // Month format options
  const monthFormat = new Intl.DateTimeFormat("en", {
    month: "long",
    year: "numeric",
  });

  for (const file of files) {
    // Skip folders from date grouping
    if (file.type === "folder") {
      if (!groups.folders) {
        groups.folders = [];
      }
      groups.folders.push(file);
      continue;
    }

    // If no date, put in "undated" group
    if (!file.date) {
      if (!groups.undated) {
        groups.undated = [];
      }
      groups.undated.push(file);
      continue;
    }

    const fileDate = new Date(file.date);

    // Check if within last 7 days
    if (fileDate >= oneWeekAgo) {
      groups.recent.push(file);
      continue;
    }

    // Group by month
    const monthYear = monthFormat.format(fileDate);
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(file);
  }

  return groups;
};

export const unhideStorageFile = async (filePath: string): Promise<boolean> => {
  try {
    const fileRef = ref(storage, filePath);

    const metadata = await getMetadata(fileRef);
    const { hidden, hiddenAt, hiddenBy, ...remainingMetadata } =
      metadata.customMetadata || {};

    await updateMetadata(fileRef, {
      ...metadata,
      customMetadata: remainingMetadata,
    });

    return true;
  } catch (error) {
    console.error(`Error unhiding file at ${filePath}:`, error);
    return false;
  }
};

// Modify the existing getAllFiles function to include hidden status and a max depth
async function getAllFiles(
  directory: StorageReference,
  currentPath = "",
  depth = 0,
  maxDepth = 20,
): Promise<MediaFile[]> {
  try {
    // Return empty array if we've exceeded the maximum depth
    if (depth > maxDepth) {
      console.warn(`Max depth (${maxDepth}) exceeded for path: ${currentPath}`);
      return [];
    }

    const result = await listAll(directory);

    // If the directory is empty (no items or prefixes), return early
    if (result.items.length === 0 && result.prefixes.length === 0) {
      return [];
    }

    const filePromises = result.items.map(async (item) => {
      try {
        const metadata = await getMetadata(item);
        // Only filter if we're not including hidden files
        if (metadata.customMetadata?.hidden === "true") {
          return null;
        }

        const url = await getDownloadURL(ref(storage, item.fullPath));
        const size = metadata.size || 0;
        const name = item.name;
        const path = item.fullPath;
        const relativePath = currentPath ? `${currentPath}/${name}` : name;

        return {
          id: item.fullPath,
          name,
          alt: metadata.customMetadata?.alt || "",
          copyright: metadata.customMetadata?.copyright || "",
          path,
          url,
          relativePath,
          size,
          type: metadata.contentType,
          date: metadata.timeCreated,
        } as MediaFile;
      } catch (error) {
        console.error(`Error processing file ${item.name}:`, error);
        return null;
      }
    });

    // Process subfolders - only if we have items to process
    const subfolderPromises = result.prefixes.map(async (prefix) => {
      const folderName = prefix.name;
      // Skip known virtual folders and any paths that match the current path to prevent loops
      if (folderName === "resized" || prefix.fullPath === directory.fullPath) {
        return null;
      }

      const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;

      // Verify this is a valid folder before recursing
      try {
        // Check if this subfolder is valid by checking its metadata
        const folderPrefix = ref(storage, prefix.fullPath);
        const subfolderFiles = await getAllFiles(
          folderPrefix,
          newPath,
          depth + 1,
          maxDepth,
        );
        return subfolderFiles;
      } catch (error) {
        console.warn(`Skipping invalid directory: ${prefix.fullPath}`, error);
        return null;
      }
    });

    // Combine results
    const filesResults = await Promise.all(filePromises);
    const subfolderResults = await Promise.all(subfolderPromises);

    return [...filesResults, ...subfolderResults]
      .flat()
      .filter((file) => file !== null) as MediaFile[];
  } catch (error) {
    console.error("Error in recursive file fetch:", error);
    return [];
  }
}

// Define MediaBrowser as FC
const MediaBrowserComponent: React.FC<MediaBrowserProps> = ({
  basePath,
  saveToPath, // relative path to save the file to
  currentImageUrl,
  fileTypeLabel,
  className,
  maxItems = 1,
  fileTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"],
  selectedFiles,
  setSelectedFiles,
  fileCacheHook,
  project,
}) => {
  const { fileCache, updateCachePath, addFilesToCache } = fileCacheHook;
  // States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const [uploadInProgress, setUploadInProgress] = useState<{
    count: number;
    value: boolean;
  }>({
    count: 0,
    value: false,
  });
  const [currentPath, setCurrentPath] = useState<string>("");

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  // Hooks
  const { t, i18n } = useTranslation();

  // Add a local attempts counter instead of using global variable
  const [fetchAttempts, setFetchAttempts] = useState(0);

  // 1. ERROR HANDLING - Move this to the top
  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setShowErrorToast(true);
    // Auto-hide error toast after 5 seconds
    setTimeout(() => setShowErrorToast(false), 5000);
  }, []);

  // 2. UTILITY FUNCTIONS
  const countItemsInFolder = useCallback(
    async (folderRef: StorageReference) => {
      const result = await listAll(folderRef);
      return result.items.length + result.prefixes.length;
    },
    [],
  );

  const getParentPath = useCallback((path: string) => {
    const parts = path.split("/");
    parts.pop();
    return parts.join("/");
  }, []);

  const getLabel = useCallback(
    (n = 1) => {
      return t(getFileTypeLabel(n, fileTypes, fileTypeLabel));
    },
    [fileTypeLabel, fileTypes, t],
  );

  // 3. Navigation handlers
  const handleBack = useCallback(() => {
    const pathParts = currentPath.split("/");
    pathParts.pop();
    setCurrentPath(pathParts.join("/"));
  }, [currentPath]);

  const handleClickInput = useCallback(() => {
    inputRef?.current?.click();
  }, []);

  // 5. File handling functions
  const isFileTypeAllowed = useCallback(
    (file: MediaFile) => {
      // Folders are always allowed
      if (file.type === "folder") return true;

      // If no file types specified, all files are allowed
      if (!fileTypes || fileTypes.length === 0) return true;

      // Check if the file's type matches any of the allowed types
      return fileTypes.some((allowedType) => {
        // For exact match
        if (file.type === allowedType) return true;

        // For wildcard matches like "image/*"
        if (allowedType.endsWith("/*")) {
          const category = allowedType.split("/")[0];
          return file.type.startsWith(`${category}/`);
        }

        return false;
      });
    },
    [fileTypes],
  );

  // 8. Selection handlers
  const handleFileSelectMemoized = useCallback(
    (file: MediaFile) => {
      if (file.type === "folder") {
        setCurrentPath(file.relativePath || "");
        return;
      }

      // Don't allow selection of files with disallowed types
      if (!isFileTypeAllowed(file)) return;

      if (maxItems > 1) {
        setSelectedFiles((prev) => {
          const isSelected = prev.some((e) => e.url === file.url);
          const newSelection = isSelected
            ? prev.filter(({ url }) => url !== file.url)
            : [...prev, { ...file, alt: file.alt || "", size: file.size || 0 }];
          return newSelection;
        });
      } else {
        setSelectedFiles(([prev]) => {
          if (prev?.url === file.url) return [];
          return [{ ...file, alt: file.alt || "", size: file.size || 0 }];
        });
      }
    },
    [maxItems, isFileTypeAllowed, setSelectedFiles],
  );

  // 9. File upload handling
  const handleFileAddition = useCallback(
    async (files: FileList | File[]) => {
      if (!files || files.length === 0) return;

      // Filter out files that don't match allowed types
      const allowedFiles = Array.from(files).filter((file) => {
        return (
          fileTypes.length === 0 ||
          fileTypes.some((type) => {
            if (type.endsWith("/*")) {
              const category = type.split("/")[0];
              return file.type.startsWith(`${category}/`);
            }
            return file.type === type;
          })
        );
      });

      if (allowedFiles.length === 0) {
        handleError(t("error:unsupportedFileType"));
        return;
      }

      setUploadInProgress({
        count: allowedFiles.length,
        value: true,
      });
      setLoading(fileCache[basePath]?.length === 0);
      try {
        const newFiles: MediaFile[] = [];

        // Process each file
        for (let i = 0; i < allowedFiles.length; i++) {
          const file = allowedFiles[i];

          // Determine the path to save the file
          const uploadPath = (() => {
            // If saveToPath is defined, use it (relative to basePath)
            if (saveToPath) {
              return `${basePath}/${saveToPath}`.replace(/\/+/g, "/");
            }

            // Otherwise use the current path
            return currentPath
              ? `${basePath}/${currentPath}`
              : basePath.replace(/\/$/, "");
          })();

          // Check if the file is an image and smaller than threshold
          let processedFile = file;
          if (file.type.startsWith("image/") && file.type !== "image/png") {
            const img = new Image();
            const imgUrl = URL.createObjectURL(file);

            await new Promise((resolve) => {
              img.onload = resolve;
              img.src = imgUrl;
            });

            // If image is smaller than 512x512, upscale it
            if (img.width < 512 || img.height < 512) {
              try {
                const base64 = await new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(file);
                });

                const upscaledBase64 = await handleUpscale(base64);
                if (upscaledBase64) {
                  const response = await fetch(upscaledBase64);
                  const blob = await response.blob();
                  processedFile = new File([blob], file.name, {
                    type: file.type,
                  });
                }
              } catch (error) {
                console.warn("Failed to upscale image:", error);
              }
            }

            URL.revokeObjectURL(imgUrl);
          }

          const [object] = await getMediaObjects({
            fileList: [processedFile],
            setPath: () => uploadPath,
            options: {
              addToCache: false,
              metadata: {
                customMetadata: {
                  lang:
                    project?.settings?.originalLanguage ||
                    i18n.resolvedLanguage ||
                    "de",
                },
              },
            },
          });

          // Create a new MediaFile object
          const newFile: MediaFile = {
            id: `${object.path}/${object.name}`,
            name: object.name,
            path: `${object.path}/${object.name}`,
            type: file.type,
            url: object.url, // Create a temporary URL
            size: file.size,
            thumbnail: object.url,
            alt: "", // Default empty string for alt
            date: new Date().toISOString(),
          };

          // Handle alt text if available
          if (object && typeof (object as MediaObject).alt === "string") {
            newFile.alt = (object as MediaObject).alt || "";
          }

          newFiles.push(newFile);
        }

        // Use addFilesToCache with the sort function
        addFilesToCache(basePath, newFiles, sortMediaBrowserFiles);

        // Map MediaFile[] to ImageInput[]
        const newSelectedFiles: ImageInput[] = newFiles.map((nf) => ({
          ...nf,
          alt: nf.alt || "",
          size: nf.size || 0,
        }));

        // Select the newly added files
        if (maxItems > 1) {
          setSelectedFiles((prev) => [...prev, ...newSelectedFiles]);
        } else if (newFiles.length > 0) {
          setSelectedFiles([newSelectedFiles[0]]);
        }
        document
          .getElementById(newFiles[0].url)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (error) {
        console.error("Error adding files:", error);
        handleError(t("error:fileUploadFailed"));
      } finally {
        setLoading(false);
        setUploadInProgress((e) => ({
          ...e,
          value: false,
        }));
      }
    },
    [
      basePath,
      currentPath,
      maxItems,
      fileTypes,
      t,
      handleError,
      fileCache,
      saveToPath,
      setSelectedFiles,
      addFilesToCache,
      project?.settings?.originalLanguage,
      i18n.resolvedLanguage,
    ],
  );

  // 10. Fetch files
  const fetchFiles = useCallback(
    async (force = false) => {
      setSelectedFiles([]);
      // Check if this path is already in the cache
      if (fileCache[basePath] && !force) return;

      setLoading(true);
      setError(null);
      setShowErrorToast(false);

      try {
        const storageRef = ref(storage, basePath);

        // Get all files recursively
        const allFiles = await getAllFiles(storageRef, currentPath);
        const sortedFiles = allFiles
          .sort(sortMediaBrowserFiles)
          .filter(
            (file) =>
              !file.hidden && (file.type === "folder" || isFileTypeAllowed(file)),
          );

        // Use the hook's updateCachePath function
        updateCachePath(basePath, sortedFiles);
      } catch (err) {
        console.error("Error fetching files:", err);
        handleError(err instanceof Error ? err.message : t("error:loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [
      basePath,
      t,
      handleError,
      fileCache,
      currentPath,
      isFileTypeAllowed,
      updateCachePath,
      setSelectedFiles,
    ],
  );

  // 11. Memoize computed values
  const fileGroups = useMemo(() => {
    return groupFilesByDate(fileCache[basePath] || []);
  }, [fileCache, basePath]);

  const sortedGroupKeys = useMemo(() => {
    return Object.keys(fileGroups || {}).sort((a, b) => {
      // Keep special categories at the top
      if (a === "folders") return -1;
      if (b === "folders") return 1;
      // For regular months, sort newest first
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }, [fileGroups]);

  // Create grouped items for display
  const groupedItems = useMemo(() => {
    const items: Array<{
      key: string;
      text: string;
      files: MediaFile[];
    }> = [];

    // Process each group and prepare grouped structure
    for (const key of sortedGroupKeys) {
      const groupFiles = fileGroups[key];
      if (groupFiles.length === 0) continue;

      // Determine the heading text
      let headingText = key;
      if (!Number.isNaN(Date.parse(key))) {
        headingText = new Date(key).toLocaleDateString(i18n.language, {
          month: "long",
          year: "numeric",
        });
      }
      if (key === "recent") {
        headingText = t("global:lastDays", { count: 7 });
      } else if (key === "folders") {
        headingText = t("global:folders");
      } else if (key === "undated") {
        headingText = t("global:noDate");
      }

      // Only create headers for groups with enough files
      if (groupFiles.length > 0) {
        items.push({
          key,
          text: headingText,
          files: groupFiles,
        });
      }
    }

    return items;
  }, [
    fileGroups,
    sortedGroupKeys,
    t,
    i18n.language,
  ]);

  // 13. Memoize UI components
  const fileDropArea = useMemo(
    () => (
      <FileDrop
        onDrop={handleFileAddition}
        showImage={false}
        multiple={maxItems > 1}
        fileTypes={fileTypes}
        loading={loading}
        className={fileDropStyles()}
        hidden={(fileCache[basePath] || []).length > 0 || loading}
      />
    ),
    [handleFileAddition, maxItems, fileTypes, loading, fileCache, basePath],
  );

  const loadingIndicator = useMemo(
    () =>
      loading ? (
        <div className={loadingOverlayStyles()}>
          <Icon name="loading" className="h-10 w-auto" />
        </div>
      ) : null,
    [loading],
  );

  // Then, in the MediaBrowser component, add this memoized value
  const selectedFilesMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const { url } of selectedFiles) {
      map[url] = true;
    }
    return map;
  }, [selectedFiles]);

  // 14. Memoize the file grid
  const FileGrid = useMemo(() => {
    if (loading || (fileCache[basePath] || []).length === 0) return null;
    return (
      <>
        {/* Display each section with its own header and grid */}
        {groupedItems.map((group) => (
          <div key={group.key} className="first:-mt-5">
            {/* Section Header */}
            <h2 className={sectionHeaderStyles()}>{group.text}</h2>

            {/* Files Grid for this section */}
            <div className="grid grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5 gap-5 mt-3 pb-5">
              {group.files.map((file) => (
                <MemoizedFileItem
                  key={file.url}
                  file={file}
                  isSelected={!!selectedFilesMap[file.url]}
                  disabled={!isFileTypeAllowed(file)}
                  basePath={basePath}
                  onSelect={handleFileSelectMemoized}
                />
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }, [
    loading,
    fileCache,
    basePath,
    groupedItems,
    selectedFilesMap,
    isFileTypeAllowed,
    handleFileSelectMemoized,
  ]);

  // 16. Effects
  // biome-ignore lint/correctness/useExhaustiveDependencies: should not run when selectedFiles.length changes
  const normalizeUrl = useCallback((url: string) => {
    try {
      const u = new URL(url);
      return `${u.origin}${u.pathname}`;
    } catch {
      return url.split("?")[0]?.split("#")[0] || url;
    }
  }, []);

  const selectCurrentImage = useCallback(() => {
    if (!currentImageUrl) return;
    const target = normalizeUrl(currentImageUrl);
    const matchingFile = (fileCache[basePath] || []).find(
      (file) => normalizeUrl(file.url) === target,
    );
    if (matchingFile && selectedFiles.length === 0) {
      setSelectedFiles([
        {
          url: matchingFile.url,
          alt: matchingFile.alt || "",
          name: matchingFile.name,
          size: matchingFile.size || 0,
          type: matchingFile.type,
        },
      ]);
      document
        .getElementById(matchingFile.url)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [
    currentImageUrl,
    fileCache,
    basePath,
    normalizeUrl,
    selectedFiles.length,
    setSelectedFiles,
  ]);

  useEffect(() => {
    selectCurrentImage();
  }, [selectCurrentImage]);

  useEffect(() => {
    // If we have the path in cache, use that immediately
    if (!fileCache[basePath] && fetchAttempts < 3) {
      setFetchAttempts((prev) => prev + 1);
      fetchFiles().then(() => {
        setLoading(false);
        selectCurrentImage();
      });
    }
  }, [fileCache, basePath, fetchFiles, selectCurrentImage, fetchAttempts]);

  return (
    <div className={containerStyles({ className })}>
      <Button
        leadingIcon="upload"
        variant="primary"
        onClick={handleClickInput}
        leadingIconProps={{
          className: "w-[1.125em] h-[1.125em] -translate-x-[1px]",
        }}
        className="whitespace-nowrap"
        classNameWrapper="fixed top-16 right-3 z-50"
      >
        {t("util:upload", { val: getLabel(maxItems) })}
      </Button>
      {!loading && fileCache[basePath]?.length > 0 && (
        <Button
          leadingIcon="refresh"
          variant="shapeless"
          onClick={() => fetchFiles(true)}
          leadingIconProps={{
            className: "w-[1em] h-[1em] -translate-x-[1px]",
          }}
          className="whitespace-nowrap"
          textSubdued
          classNameWrapper="absolute top-1.5 right-3 z-50"
          size="sm"
        >
          {t("util:refresh")}
        </Button>
      )}

      {/* Header */}
      {!!currentPath && (
        <div className="p-5 border-b border-border-subdued items-center flex justify-between sticky top-0 w-full gap-5 z-20 bg-surface-default">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              leadingIcon="arrow-left-md"
              disabled={currentPath === ""}
              onClick={handleBack}
            >
              {t("global:back")}
            </Button>
            {currentPath.split("/").map((part, i, arr) => (
              <React.Fragment key={`path-${arr.slice(0, i + 1).join("/")}`}>
                <span
                  className={cn(
                    "text-sm truncate cursor-default",
                    i !== arr.length - 1 && "cursor-pointer hover:underline",
                  )}
                  onClick={() => {
                    if (i !== arr.length - 1) {
                      setCurrentPath(arr.slice(0, i + 1).join("/"));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setCurrentPath(arr.slice(0, i + 1).join("/"));
                    }
                  }}
                >
                  {part}
                </span>
                {i < arr.length - 1 && (
                  <span className="text-sm text-text-placeholder font-bold">
                    /
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <input
        type="file"
        ref={inputRef}
        className="hidden"
        onChange={(e) => e.target.files && handleFileAddition(e.target.files)}
        multiple={maxItems > 1}
        accept={fileTypes?.join(",")}
      />
      {fileDropArea}
      {loadingIndicator}

      {/* File Grid with container to handle action bar positioning */}
      <div className="relative flex flex-col flex-1 overflow-hidden">
        {!loading && (fileCache[basePath] || []).length > 0 && (
          <div
            className={cn(
              "p-5 overflow-auto flex-1 pb-0 duration-500 ease-expressive-out",
              selectedFiles.length > 0 && "mb-20",
            )}
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSelectedFiles([]);
              }
            }}
          >
            {FileGrid}
          </div>
        )}
      </div>

      {/* Toasts */}
      <Toast
        id="uploadingMediaFiles"
        leadingIcon={uploadInProgress.value ? "loading" : "checkmark-lg"}
        leadingIconClassName={uploadInProgress.value ? "" : "text-icon-success"}
        show={uploadInProgress.value}
      >
        {uploadInProgress.value
          ? t("util:upload", { val: getLabel(uploadInProgress.count) })
          : t("util:uploaded", {
              val: getLabel(uploadInProgress.count),
            })}
      </Toast>
    </div>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders when modal stack changes
const MediaBrowser = React.memo(MediaBrowserComponent);

// Export the component and also the delete function directly for use in other components
export default React.memo(MediaBrowser);
